// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { DealerProductEnrollments } from '@interfaces/dealer.interfaces';
import { Assignees, DdjDocument, DdjDocumentCore, FieldTypes } from '@interfaces/document.interfaces';
import { EditField, EditSection } from '@interfaces/interfaces';
import { DealerHomePage, OEM, Tenant, UserPermissionIds } from '@interfaces/user.interfaces';
import deepClone from 'deep-clone';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';

export const bufferArrayToBase64 = (bufferArray: ArrayBuffer) => {
    let binary = '';
    const bytes = new Uint8Array(bufferArray);
    const length = bytes.byteLength;

    for (let i = 0; i < length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }

    return window.btoa(binary);
};

export const getDealerHomePage = (defaultPageId: any, isCustomer: boolean, permissions: any) => {
    //ToDo: This needs to be added to the DefaultPage table to be truely dynamic and table driven. Several apis will need to be updated to allow for this.
    if (isCustomer) {
        return ['/main', 'vehicles'];
    }

    switch (defaultPageId) {
        case DealerHomePage.leads.toString():
            return ['/dashboard', 'leads'];
        case DealerHomePage.consumerDocuments.toString():
            return ['/dashboard', 'documents'];
        case DealerHomePage.deals.toString(): {
            let hasPermission = false;
            //check permission and bounce to leads if false
            if (typeof permissions === 'string') {
                const arr = permissions.split(',');
                hasPermission = arr.includes(UserPermissionIds.Deals.toString());
            } else if (Array.isArray(permissions) && permissions.length > 0) {
                hasPermission = permissions[0]?.userPermissionId.includes(UserPermissionIds.Deals);
            }

            return hasPermission ? ['/dashboard', 'deals'] : ['/dashboard', 'leads'];
        }
        default:
            return ['/dashboard', 'leads'];
    }
};

export const scrollIntoView = (cssSelector: string, pause = 10) => {
    // Sometimes we need to wait for element to be created
    of(null)
        .pipe(delay(pause))
        .subscribe(() => {
            const scrollableElement = document.querySelector(cssSelector);
            if (scrollableElement) {
                scrollableElement.scrollIntoView({
                    block: 'center',
                    behavior: 'smooth',
                });
            }
        });
};

export const getHtmlCenter = (el: HTMLElement, pdfView = false): { x: number; y: number } => {
    const pos = el.getBoundingClientRect();

    let x, y;
    if (pdfView) {
        x = pos.width / 2;
        y = pos.height / 2;
    } else {
        x = pos.x + pos.width / 2;
        y = pos.y + pos.height / 2;
    }
    return { x, y };
};

export function sortByDisplayName<T extends DdjDocument | DdjDocumentCore>(documents: T[]): T[] {
    return documents.sort((a, b) => (a.displayName?.toLocaleLowerCase().trim() > b.displayName?.toLocaleLowerCase().trim() ? 1 : -1));
}

//Any additional calls to this function please re-evaluate the use of only using user-agent.
export const isMobileOrTablet = (): boolean => {
    const ua = navigator.userAgent;
    if (
        /(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua) ||
        /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)
    ) {
        return true;
    }
    //TEMPORARILY including this code to detect Ipad because apple removed 'ipad' from the user agent string
    //We will change to look if specific features are available for mobile instead of looking at the user agent after 7-10-2023
    //This will also be fooled by a desktop ios that has a touch screen
    //In the case of driver's license the worst that will happen is they will try to do the scans using their desktop webcam which may or may not have the resolution to do so
    else if (/Macintosh/i.test(ua) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1) {
        return true;
    }
    return false;
};

export const getEditFieldsSections = (includeSpecial = false): EditSection[] => {
    return [
        {
            type: Assignees.buyer,
            label: 'Buyer',
            fields: getEditFields(Assignees.buyer),
            isOpen: true,
        },
        {
            type: Assignees.cobuyer,
            label: 'Co-Buyer',
            fields: getEditFields(Assignees.cobuyer),
            isOpen: false,
        },
        {
            type: Assignees.dealer,
            label: 'Dealer',
            fields: getEditFields(Assignees.dealer),
            isOpen: false,
        },
        {
            type: Assignees.deal,
            label: 'Deal',
            fields: getDealEditFields(includeSpecial),
            isOpen: false,
        },
    ];
};

export const getShortenedVideoDesc = (fullDesc: string) => {
    const shortDescArr = fullDesc.split('').slice(0, 200);
    return shortDescArr.join('');
};

export const getEditFields = (assignee: Assignees): EditField[] => {
    const fields = [
        {
            assigneeType: assignee,
            fieldType: FieldTypes.signature,
            label: 'Add Signature',
        },
        {
            assigneeType: assignee,
            fieldType: FieldTypes.initials,
            label: 'Add Initials',
        },
        {
            assigneeType: assignee,
            fieldType: FieldTypes.legalName,
            label: 'Add Legal Name',
        },
        {
            assigneeType: assignee,
            fieldType: FieldTypes.date,
            label: 'Add Date',
        },
        {
            assigneeType: assignee,
            fieldType: FieldTypes.requiredText,
            label: 'Add Required Text Field',
        },
        {
            assigneeType: assignee,
            fieldType: FieldTypes.optionalText,
            label: 'Add Optional Text Field',
        },
        {
            assigneeType: assignee,
            fieldType: FieldTypes.optionalSignature,
            label: 'Add Optional Signature',
        },
        {
            assigneeType: assignee,
            fieldType: FieldTypes.optionalInitials,
            label: 'Add Optional Initials',
        },
        {
            assigneeType: assignee,
            fieldType: FieldTypes.optionalLegalName,
            label: 'Add Optional Legal Name',
        },
    ];
    if (assignee === Assignees.dealer) {
        fields.push({
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.jobTitle,
            label: 'Job Title',
        });
    }
    return fields;
};

export const getDealEditFields = (includeSpecial = false): EditField[] => {
    const fields = [
        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.vin,
            label: 'Add VIN',
        },
        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.last8Vin,
            label: 'Add Last 8 of VIN',
        },
        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.vehicleYear,
            label: 'Add Vehicle Year',
        },
        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.vehicleMake,
            label: 'Add Vehicle Make',
        },
        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.vehicleModel,
            label: 'Add Vehicle Model',
        },
        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.vehicleTrim,
            label: 'Add Vehicle Trim',
        },
        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.buyerName,
            label: 'Add Buyer Name',
        },
        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.cobuyerName,
            label: 'Add Co-Buyer Name',
        },
        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.dealershipName,
            label: 'Add Dealership Name',
        },
        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.dealerContact,
            label: 'Add F&I Manager',
        },
        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.dealNumber,
            label: 'Add Deal #',
        },
        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.tradeInVehicleYear,
            label: 'Add Trade-In Vehicle Year',
        },
        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.tradeInVehicleMake,
            label: 'Add Trade-In Vehicle Make',
        },
        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.tradeInVehicleModel,
            label: 'Add Trade-In Vehicle Model',
        },
        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.tradeInVehicleTrim,
            label: 'Add Trade-In Vehicle Trim',
        },
        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.tradeInVehiclePayoff,
            label: 'Add Trade-In Vehicle Payoff',
        },
        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.stockId,
            label: 'Add Stock ID',
        },

        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.tradeInVin,
            label: 'Add Trade-In VIN',
        },

        {
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.mileage,
            label: 'Add Mileage',
        },
    ];

    if (includeSpecial) {
        fields.push({
            assigneeType: Assignees.deal,
            fieldType: FieldTypes.listOfDocuments,
            label: 'Add List Of Signed Documents',
        });
    }

    return fields;
};

// Because "clone" is neater and deepClone has a weird default export that makes the linter all fussy.
// Also, be aware, this is not very performant.
export const clone = deepClone;

// returns true if current url contains stellantis
// will need to update for new tenants
export const tenantIsStellantis = (oemCode?: string): boolean => {
    //OEMCode should be first priority if exists
    if (oemCode) {
        return OEM.stellantis === oemCode;
    } else {
        return window.location.href.includes(Tenant.stellantis) || window.location.href.includes(Tenant.stellantisEZDOCS);
    }
};

export const removeNonNumericCharacters = (value: string): string => {
    return value.replace(/\D/g, '');
};

export const getImage = async (vehicleInfo: any): Promise<string> => {
    let vehicleImages: string[] = [];
    let checkedImage: string | null = null;

    // Extract vehicle images array or use .image
    if (vehicleInfo?.vehicle) {
        if (!vehicleInfo.vehicle?.vehicleExt?.image && !vehicleInfo.vehicle?.vehicleExt?.images) {
            checkedImage = getMissingImage();
        }

        if (vehicleInfo.vehicle?.vehicleExt?.image) {
            checkedImage = vehicleInfo.vehicle?.vehicleExt?.image;
        } else {
            vehicleImages = vehicleInfo.vehicle?.vehicleExt?.images?.split(',');
        }
    } else {
        if (!vehicleInfo?.vehicleExt?.image && !vehicleInfo?.vehicleExt?.images) {
            checkedImage = getMissingImage();
        }

        if (vehicleInfo?.vehicleExt?.image) {
            checkedImage = vehicleInfo?.vehicleExt?.image;
        } else {
            vehicleImages = vehicleInfo?.vehicleExt?.images?.split(',');
        }
    }

    // Map over images until a check succeeds
    if (!!vehicleImages && vehicleImages?.length !== 0) {
        for (const image of vehicleImages) {
            const imgSrc = image.trim();
            const isValid = await checkImage(imgSrc);
            if (isValid) {
                checkedImage = imgSrc;
                break; // Stop iterating once a valid image is found
            } else {
                checkedImage = getMissingImage();
            }
        }
    }

    // Return successful image or missing image
    return checkedImage;
};

const checkImage = async (imgSrc: string): Promise<boolean> => {
    return new Promise((resolve) => {
        const image = new Image();

        image.onload = () => {
            resolve(true); // Image loaded successfully
        };

        image.onerror = () => {
            resolve(false); // Image failed to load
        };

        image.src = imgSrc;
    });
};

export const getMissingImage = (): string => {
    return 'assets/img/mysteryVehicle.png';
};

export const showMissingImage = (event: Event) => {
    (<HTMLImageElement>event.target).src = getMissingImage();
};

export const toTitleCase = (text: string) => {
    return `${text.slice(0, 1).toUpperCase()}${text.slice(1, text.length)}`;
};

export const formattedDate = (date: Date, includeTime: boolean) => {
    const newDate = new Date(date).toLocaleDateString('en-US');
    const newTime = new Date(date).toLocaleTimeString('en-US');

    if (includeTime) {
        return `${newDate} at ${newTime}`;
    } else {
        return newDate;
    }
};

export const getVehicleDisplayDescription = (make: string, model: string, year: any, trim: string = null): string => {
    return model ? (year > 0 ? year : '') + (make ? ' ' + make : '') + ' ' + model : '';
};

export const getEnrollmentStatus: (product: DealerProductEnrollments) => boolean = (product: DealerProductEnrollments) => {
    const currentDate = new Date();

    if (product.activationDate) {
        if (product.turnOffAccessDate) {
            if (currentDate < new Date(product.turnOffAccessDate)) {
                return true;
            }
            return false;
        }
        return true;
    }
    return false;
};

export const fileSizeInMegaBytes = (bytes: number): number => {
    return bytes / (1024 * 1024);
};

export const fileSizeInString = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';

    const kb = (bytes / 1024).toFixed(0);

    if (parseInt(kb) < 1024) {
        return kb + ' KB';
    } else {
        const mb = (parseInt(kb) / 1024).toFixed(0);
        return mb + ' MB';
    }
};
